import { useState } from "react";
import { routes } from "@/router";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { HiEllipsisHorizontal, HiOutlineMoon, HiOutlinePencilSquare, HiOutlineSun } from "react-icons/hi2";
import { generatePath, useNavigate } from "react-router-dom";

import { Campaign } from "@/hooks/api/predictive/product/useGetAllProducts";
import Modal from "@/components/molecules/Modal";
import ReactivateCampaignModal from "@/components/molecules/predictive/ReactivateCampaignModal";
import SetCampaignInactiveModal from "@/components/molecules/predictive/SetCampaignInactiveModal";

type CampaignCardSettingsProps = {
  productId: string;
  campaign: Campaign;
};

const CampaignCardSettings = ({ productId, campaign }: CampaignCardSettingsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpenModal, setIsOpenModal] = useState<"SET_INACTIVE" | "SET_ACTIVE" | null>(null);

  const edit = () => {
    navigate(generatePath(routes.campaign.update, { productId, campaignId: campaign.id }));
  };

  return (
    <>
      <Menu>
        <div>
          <MenuButton className={"p-1"}>
            <HiEllipsisHorizontal className="size-5" />
          </MenuButton>
        </div>

        <MenuItems
          transition
          anchor="bottom end"
          className="w-52 origin-top-right rounded-lg border border-secondary bg-white py-1 shadow-sm transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
        >
          <MenuItem>
            <button
              onClick={edit}
              className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-primary-hover"
            >
              <HiOutlinePencilSquare className="size-6" />
              {t("predictive.campaign.settings.edit")}
            </button>
          </MenuItem>
          {campaign.status === "ACTIVE" && !campaign.canceled ? (
            <MenuItem>
              <>
                <button
                  onClick={() => setIsOpenModal("SET_INACTIVE")}
                  className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-primary-hover"
                >
                  <HiOutlineMoon className="size-6" />
                  {t("predictive.campaign.settings.setInactive")}
                </button>
              </>
            </MenuItem>
          ) : (
            <MenuItem>
              <>
                <button
                  onClick={() => setIsOpenModal("SET_ACTIVE")}
                  className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-primary-hover"
                >
                  <HiOutlineSun className="size-6" />
                  {t("predictive.campaign.settings.setActive")}
                </button>
              </>
            </MenuItem>
          )}
        </MenuItems>
      </Menu>
      {campaign.status === "ACTIVE" && !campaign.canceled ? (
        <Modal
          onClose={() => setIsOpenModal(null)}
          isOpen={isOpenModal === "SET_INACTIVE"}
          content={SetCampaignInactiveModal}
          contentProps={{
            endDate: campaign.period_end,
            campaignId: campaign.id,
            onSuccess: () => setIsOpenModal(null),
          }}
        />
      ) : (
        <Modal
          onClose={() => setIsOpenModal(null)}
          isOpen={isOpenModal === "SET_ACTIVE"}
          content={ReactivateCampaignModal}
          contentProps={{
            campaignId: campaign.id,
            onSuccess: () => setIsOpenModal(null),
          }}
        />
      )}
    </>
  );
};

export default CampaignCardSettings;
